import { ChangeDetectionStrategy, Component } from '@angular/core';

interface Wave {
  path: string;
  speed: number;
  opacity: number;
}

@Component({
  selector: 'lib-background',
  standalone: true,
  templateUrl: './background.component.html',
  styleUrls: ['./background.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: block' },
})
export class BackgroundComponent {
  waves: Wave[] = this.generateWaves(3, 0);
  wavesTopLeft: Wave[] = this.generateWaves(3, 10);

  private generateWaves(count: number, seedOffset: number): Wave[] {
    return Array.from({ length: count }, (_, i) => ({
      path: this.generateSmoothWavePath(i + seedOffset),
      speed: 60 + i * 10,
      opacity: 0.7 - i * 0.2,
    }));
  }

  private generateSmoothWavePath(seed: number): string {
    const width = 1440;
    const points = 6;
    const segmentWidth = width / points;
    const midY = 50;
    const amplitude = 25 + (seed % 5) * 3;

    const yValues: number[] = [];
    for (let i = 0; i < points; i++) {
      const phase = seed * 1.8 + i * 1.3;
      yValues.push(midY + Math.sin(phase) * amplitude);
    }
    yValues.push(yValues[0]);

    let d = `M0,${yValues[0]}`;
    for (let i = 0; i < points; i++) {
      const x0 = i * segmentWidth;
      const x1 = (i + 1) * segmentWidth;
      const y0 = yValues[i];
      const y1 = yValues[i + 1];
      const cpx1 = x0 + segmentWidth / 3;
      const cpx2 = x1 - segmentWidth / 3;
      d += ` C${cpx1},${y0} ${cpx2},${y1} ${x1},${y1}`;
    }

    const bottomY = 85;
    const bottomAmp = 10 + (seed % 5) * 2;
    const bottomValues: number[] = [];
    for (let i = 0; i <= points; i++) {
      const phase = seed * 2.5 + i * 1.1;
      bottomValues.push(bottomY + Math.sin(phase) * bottomAmp);
    }
    bottomValues[points] = bottomValues[0];

    d += ` L${width},${bottomValues[points]}`;
    for (let i = points - 1; i >= 0; i--) {
      const x0 = (i + 1) * segmentWidth;
      const x1 = i * segmentWidth;
      const y0 = bottomValues[i + 1];
      const y1 = bottomValues[i];
      const cpx1 = x0 - segmentWidth / 3;
      const cpx2 = x1 + segmentWidth / 3;
      d += ` C${cpx1},${y0} ${cpx2},${y1} ${x1},${y1}`;
    }
    d += ' Z';
    return d;
  }
}
